// src/services/StripeConnectService.js
import { getFunctionURL } from './PaymentService';
import { updateUserStripeAccount, STRIPE_ONBOARDING_STATUS } from './UserRolesService';
import { auth } from '../firebase';

// Updated function URLs based on your deployment output
const functionUrls = {
  createPaymentIntent: 'https://createpaymentintent-x27vmbsymq-uc.a.run.app',
  createConnectedAccount: 'https://createconnectedaccount-x27vmbsymq-uc.a.run.app',
  createAccountLink: 'https://createaccountlink-x27vmbsymq-uc.a.run.app',
  getAccountStatus: 'https://getaccountstatus-x27vmbsymq-uc.a.run.app',
  createDashboardLink: 'https://createdashboardlink-x27vmbsymq-uc.a.run.app',
  getListingInfo: 'https://getlistinginfo-x27vmbsymq-uc.a.run.app'
};

/**
 * Create a Stripe Connect account for a provider
 */
export const createConnectedAccount = async (userId, email) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('User not authenticated');
    
    const idToken = await currentUser.getIdToken(true);
    
    console.log('Creating connected account for user:', userId);
    
    const response = await fetch(functionUrls.createConnectedAccount, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        email,
        idToken
      }),
    });

    const responseText = await response.text();
    console.log('Create account response:', response.status, responseText);
    
    let responseData;
    try {
      responseData = JSON.parse(responseText);
    } catch (e) {
      console.error('Failed to parse response:', responseText);
      throw new Error(`Server returned invalid response: ${responseText}`);
    }

    if (!response.ok) {
      console.error('Create account error response:', responseData);
      const errorMessage = responseData.details || responseData.error || 'Failed to create connected account';
      throw new Error(errorMessage);
    }

    const { accountId, existing } = responseData;
    
    // Update user record with Stripe account ID (only if it's a new account)
    if (!existing) {
      await updateUserStripeAccount(userId, accountId, STRIPE_ONBOARDING_STATUS.IN_PROGRESS);
    }
    
    return accountId;
  } catch (error) {
    console.error('Error creating connected account:', error);
    throw error;
  }
};

/**
 * Create an account link for Stripe Connect onboarding
 */
export const createAccountLink = async (accountId, refreshUrl, returnUrl) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('User not authenticated');
    
    const idToken = await currentUser.getIdToken(true);
    
    console.log('Creating account link for account:', accountId);
    
    const response = await fetch(functionUrls.createAccountLink, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        accountId,
        refreshUrl,
        returnUrl,
        idToken
      }),
    });

    const responseText = await response.text();
    console.log('Create account link response:', response.status);
    
    let responseData;
    try {
      responseData = JSON.parse(responseText);
    } catch (e) {
      console.error('Failed to parse response:', responseText);
      throw new Error(`Server returned invalid response: ${responseText}`);
    }

    if (!response.ok) {
      console.error('Create account link error response:', responseData);
      const errorMessage = responseData.details || responseData.error || 'Failed to create account link';
      throw new Error(errorMessage);
    }

    const { url } = responseData;
    return url;
  } catch (error) {
    console.error('Error creating account link:', error);
    throw error;
  }
};

/**
 * Check if a connected account has completed onboarding
 */
export const checkAccountStatus = async (accountId) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('User not authenticated');
    
    const idToken = await currentUser.getIdToken(true);
    
    const response = await fetch(functionUrls.getAccountStatus, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        accountId,
        idToken
      }),
    });

    const responseText = await response.text();
    
    let responseData;
    try {
      responseData = JSON.parse(responseText);
    } catch (e) {
      console.error('Failed to parse response:', responseText);
      throw new Error(`Server returned invalid response: ${responseText}`);
    }

    if (!response.ok) {
      console.error('Get account status error response:', responseData);
      const errorMessage = responseData.details || responseData.error || 'Failed to get account status';
      throw new Error(errorMessage);
    }

    const { chargesEnabled, detailsSubmitted, payoutsEnabled } = responseData;
    
    return {
      chargesEnabled,
      detailsSubmitted,
      payoutsEnabled,
      isComplete: chargesEnabled && detailsSubmitted && payoutsEnabled
    };
  } catch (error) {
    console.error('Error checking account status:', error);
    throw error;
  }
};

/**
 * Create a login link for the Express dashboard
 */
export const createDashboardLink = async (accountId) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('User not authenticated');
    
    const idToken = await currentUser.getIdToken(true);
    
    const response = await fetch(functionUrls.createDashboardLink, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        accountId,
        idToken
      }),
    });

    const responseText = await response.text();
    
    let responseData;
    try {
      responseData = JSON.parse(responseText);
    } catch (e) {
      console.error('Failed to parse response:', responseText);
      throw new Error(`Server returned invalid response: ${responseText}`);
    }

    if (!response.ok) {
      console.error('Create dashboard link error response:', responseData);
      const errorMessage = responseData.details || responseData.error || 'Failed to create dashboard link';
      throw new Error(errorMessage);
    }

    const { url } = responseData;
    return url;
  } catch (error) {
    console.error('Error creating dashboard link:', error);
    throw error;
  }
};